import { ApolloError } from '@apollo/client';

import { useGroupDetailQuery, GroupDetailQuery } from 'core/graphql';

import { useStableRefetch } from './useStableRefetch';

export type IGroupDetail = NonNullable<GroupDetailQuery['group']>;

interface IGroupDetailHook {
  group?: IGroupDetail;
  loading: boolean;
  error?: ApolloError;
  refetch: () => void;
}

/**
 * Backs `/groups/:id/details` — `group(groupId)` resolver, minimal fields.
 *
 * 🔴 `errorPolicy: 'all'` — не послабление, а условие работоспособности.
 * Денежные поля карточки (`price`, `currency`, `contributionsSum`,
 * `freeLessonsCount`) закрыты на уровне ПОЛЯ: роли без финансового доступа
 * сервер отдаёт данные группы и рядом — ошибки «нет прав на поле». При
 * умолчальной политике Apollo такой ответ считается неудачей целиком, `data`
 * остаётся пустой, и методист видел вместо карточки прочерк: ни имени, ни
 * вкладок, ни объяснения. Своей вкладки «Навыки» (ТЗ 0041) он тем более не
 * достигал — а правит их именно он.
 *
 * С `'all'` карточка показывает то, что роли видно, а закрытые поля приходят
 * пустыми — ровно так, как их и задумывал бэкенд. Финансовые блоки и без того
 * скрыты `useGroupPermissions().canViewFinance`.
 */
export const useGroupDetail = (id?: string): IGroupDetailHook => {
  const { data, loading, error, refetch } = useGroupDetailQuery({
    variables: { id: id || '' },
    skip: !id,
    fetchPolicy: 'cache-and-network',
    errorPolicy: 'all',
    notifyOnNetworkStatusChange: true,
  });

  return {
    group: data?.group ?? undefined,
    loading,
    error,
    refetch: useStableRefetch(refetch),
  };
};
